import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Lock, MoreHorizontal, Pin, PinOff, Undo2 } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { RECOMMENDATION_LABELS, SELECTED_STAGE_LABEL, type ComparisonCell, type ComparisonColumn, type ComparisonRow, type ComparisonSelection, type ComparisonStatus, type ComparisonView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Popover } from '../../../components/Popover';
import { RECOMMENDATION_TONES } from '../../../components/status-tones';
import { ToneChip, type Tone } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { EMPTY, formatDate } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER } from '../../pipeline/move-intent';
import { PENDING_OFFER_ID } from '../comparison-patch';
import type { ComparisonActions } from '../hooks/use-comparison-actions';
import styles from './Compare.module.css';

const STATUS_CHIPS: Readonly<Record<ComparisonStatus, { readonly tone: Tone; readonly label: string }>> = {
  selected: { tone: 'done', label: 'Selected' },
  finalist: { tone: 'progress', label: 'Passed final' },
  held: { tone: 'hold', label: 'On hold' },
  added: { tone: 'new', label: 'Added' },
};

const OVERALL_ROW = 'overall';
const PIN_CLASSES: readonly string[] = [styles.pinned0 ?? '', styles.pinned1 ?? '', styles.pinned2 ?? ''];

interface TableProps {
  readonly view: ComparisonView;
  readonly pins: readonly string[];
  readonly onTogglePin: (applicationId: string) => void;
  readonly actions: ComparisonActions;
}

type Menu = { readonly column: ComparisonColumn; readonly anchor: AnchorRect; readonly step: 'menu' | 'eliminate' };

/**
 * DEC-02's grid: candidates as columns, rubric lines as rows (F12.08). Each cell is the
 * line's average across rounds; opening a row shows every round's scores and comments for
 * every candidate at once, because the point is to compare. Pinned candidates move to the
 * front and stay in view while the rest scroll under them.
 */
export function ComparisonTable({ view, pins, onTogglePin, actions }: TableProps): ReactNode {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [menu, setMenu] = useState<Menu | null>(null);
  // R1 (§L34): days in process is a Time figure, drawn only while that module is on.
  const isDaysShown = useModule('time_metrics');
  const pinned = pins.flatMap((id) => view.columns.filter((column) => column.applicationId === id));
  const columns = [...pinned, ...view.columns.filter((column) => !pins.includes(column.applicationId))];

  function toggle(key: string): void {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const pinClass = (index: number): string => (index < pinned.length ? (PIN_CLASSES[index] ?? '') : '');

  return (
    <>
      <div className={styles.scroller} role="region" aria-label="Candidates side by side" tabIndex={0}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col" className={styles.labelHead}>
                <span className={styles.labelHeadText}>{columns.length === 1 ? '1 candidate' : `${columns.length} candidates`}</span>
              </th>
              {columns.map((column, index) => (
                <th key={column.applicationId} scope="col" className={`${styles.colHead} ${pinClass(index)}`}>
                  <CandidateHead
                    column={column}
                    view={view}
                    isPinned={pins.includes(column.applicationId)}
                    pending={actions.pendingOf(column.applicationId)}
                    onTogglePin={() => onTogglePin(column.applicationId)}
                    onSelect={() => actions.select(column)}
                    onUndo={() => actions.undoSelection(column)}
                    onMenu={(anchor) => setMenu({ column, anchor, step: 'menu' })}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <RowHead label="Overall" meta="Round scores, each round once" isOpen={open.has(OVERALL_ROW)} onToggle={() => toggle(OVERALL_ROW)} />
              {columns.map((column, index) => (
                <td key={column.applicationId} className={`${styles.cell} ${pinClass(index)}`}>
                  <span className={styles.big}>{column.overall === null ? EMPTY : column.overall.toFixed(2)}</span>
                  {open.has(OVERALL_ROW) && <OverallDetail column={column} />}
                </td>
              ))}
            </tr>
            {view.rows.map((row) => (
              <tr key={row.key}>
                <RowHead label={row.label} meta={`Out of ${row.scaleMax}`} isCritical={row.isCritical} isOpen={open.has(row.key)} onToggle={() => toggle(row.key)} />
                {columns.map((column, index) => {
                  const cell = column.cells.find((item) => item.key === row.key);
                  return (
                    <td key={column.applicationId} className={`${styles.cell} ${pinClass(index)}`}>
                      <Average cell={cell} row={row} />
                      {open.has(row.key) && cell && <CellDetail cell={cell} />}
                    </td>
                  );
                })}
              </tr>
            ))}
            <ExtraRow label="Test score" columns={columns} pinClass={pinClass} value={(column) => (column.assessmentPercent === null ? EMPTY : `${column.assessmentPercent.toFixed(0)}%${column.assessmentPassed === false ? ' · below pass' : ''}`)} />
            <ExtraRow
              label="Expected salary"
              columns={columns}
              pinClass={pinClass}
              value={(column) => (column.candidate.restrictedFields?.includes('expectedSalary') ? <span title="Not shown to you">{EMPTY}</span> : (column.candidate.expectedSalary ?? EMPTY))}
            />
            <ExtraRow label="Notice period" columns={columns} pinClass={pinClass} value={(column) => (column.candidate.noticePeriodDays === null ? EMPTY : `${column.candidate.noticePeriodDays} days`)} />
            <ExtraRow label="Source" columns={columns} pinClass={pinClass} value={(column) => column.sourceLabel} />
            {isDaysShown && <ExtraRow label="Days in process" columns={columns} pinClass={pinClass} value={(column) => `${column.daysInProcess} · applied ${formatDate(column.appliedAt)}`} />}
          </tbody>
        </table>
      </div>

      <Popover anchor={menu?.anchor ?? null} onClose={() => setMenu(null)} align="right" label={menu ? `${menu.column.candidate.fullName} — more` : 'More'}>
        {menu?.step === 'menu' && (
          <ColumnMenu
            column={menu.column}
            view={view}
            isPinned={pins.includes(menu.column.applicationId)}
            onPin={() => {
              onTogglePin(menu.column.applicationId);
              setMenu(null);
            }}
            onHold={() => {
              actions.hold(menu.column);
              setMenu(null);
            }}
            onEliminate={() => setMenu({ ...menu, step: 'eliminate' })}
            onRemove={() => {
              actions.remove(menu.column);
              setMenu(null);
            }}
          />
        )}
        {menu?.step === 'eliminate' && (
          <ReasonPicker
            mode="eliminate"
            reasons={view.reasons.map((reason) => ({ ...reason, minimumContactAttempts: null }))}
            pools={[]}
            canPool={false}
            contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
            candidateName={menu.column.candidate.fullName}
            onConfirm={(choice) => {
              const label = view.reasons.find((reason) => reason.code === choice.reasonCode)?.label ?? choice.reasonCode;
              actions.eliminate(menu.column, choice.reasonCode, label);
              setMenu(null);
            }}
          />
        )}
      </Popover>
    </>
  );
}

interface HeadProps {
  readonly column: ComparisonColumn;
  readonly view: ComparisonView;
  readonly isPinned: boolean;
  readonly pending: string | null;
  readonly onTogglePin: () => void;
  readonly onSelect: () => void;
  readonly onUndo: () => void;
  readonly onMenu: (anchor: AnchorRect) => void;
}

function CandidateHead({ column, view, isPinned, pending, onTogglePin, onSelect, onUndo, onMenu }: HeadProps): ReactNode {
  const chip = STATUS_CHIPS[column.status];
  const isBlocked = column.selectBlockedReason !== null;
  const hasMenu = view.can.arrange && column.status !== 'selected';
  return (
    <div className={styles.head}>
      <div className={styles.headTop}>
        <Link to="/applications/$applicationId" params={{ applicationId: column.applicationId }} className={styles.name}>
          {column.candidate.fullName}
        </Link>
        <button type="button" className={isPinned ? styles.pinOn : styles.pin} aria-pressed={isPinned} aria-label={isPinned ? `Unpin ${column.candidate.fullName}` : `Pin ${column.candidate.fullName}`} title={isPinned ? 'Unpin' : 'Pin — keep in view'} onClick={onTogglePin}>
          {isPinned ? <PinOff size={14} aria-hidden /> : <Pin size={14} aria-hidden />}
        </button>
      </div>
      <div className={styles.chips}>
        <ToneChip tone={chip.tone}>{chip.label}</ToneChip>
        {column.status === 'added' && <span className={styles.quiet}>{column.stageLabel}</span>}
        {column.hold && <span className={styles.quiet}>until {formatDate(column.hold.expiresAt)}</span>}
      </div>
      {column.selection ? (
        <div className={styles.selection}>
          <span className={styles.quiet}>
            <SelectionOffer selection={column.selection} />
            {column.selection.selectedByName ? ` · by ${column.selection.selectedByName}` : ''}
          </span>
          {column.selection.canUndo && (
            <Button variant="ghost" size="sm" icon={Undo2} isPending={pending === 'undo'} onClick={onUndo}>
              Undo
            </Button>
          )}
        </div>
      ) : (
        <div className={styles.headActions}>
          {view.can.select && (
            <Button variant="solid" size="sm" disabled={isBlocked || pending !== null} isPending={pending === 'select'} onClick={onSelect}>
              Select
            </Button>
          )}
          {hasMenu && (
            <Button variant="ghost" size="sm" icon={MoreHorizontal} aria-label={`More for ${column.candidate.fullName}`} disabled={pending !== null} onClick={(event: MouseEvent<HTMLButtonElement>) => onMenu(anchorFrom(event.currentTarget))} />
          )}
        </div>
      )}
      {view.can.select && isBlocked && column.status !== 'selected' && column.status !== 'held' && (
        <span className={styles.why}>
          <Lock size={12} aria-hidden /> {column.selectBlockedReason}
        </span>
      )}
    </div>
  );
}

function ColumnMenu({
  column,
  view,
  isPinned,
  onPin,
  onHold,
  onEliminate,
  onRemove,
}: {
  readonly column: ComparisonColumn;
  readonly view: ComparisonView;
  readonly isPinned: boolean;
  readonly onPin: () => void;
  readonly onHold: () => void;
  readonly onEliminate: () => void;
  readonly onRemove: () => void;
}): ReactNode {
  return (
    <div className={styles.menu} role="menu">
      <button type="button" role="menuitem" className={styles.menuItem} onClick={onPin}>
        {isPinned ? 'Unpin' : 'Pin — keep in view'}
      </button>
      {column.status === 'finalist' && view.can.arrange && (
        <button type="button" role="menuitem" className={styles.menuItem} onClick={onHold}>
          Hold as backup
          <span className={styles.menuHint}>30 days as a silver medalist</span>
        </button>
      )}
      {column.status === 'added' && (
        <button type="button" role="menuitem" className={styles.menuItem} onClick={onRemove}>
          Take off the comparison
          <span className={styles.menuHint}>They stay where they are in the pipeline</span>
        </button>
      )}
      {column.status !== 'added' && (
        <button type="button" role="menuitem" className={styles.menuItem} onClick={onEliminate}>
          Eliminate
          <span className={styles.menuHint}>Needs a reason; nothing is sent to them</span>
        </button>
      )}
    </div>
  );
}

function RowHead({ label, meta, isCritical = false, isOpen, onToggle }: { readonly label: string; readonly meta: string; readonly isCritical?: boolean; readonly isOpen: boolean; readonly onToggle: () => void }): ReactNode {
  return (
    <th scope="row" className={styles.rowHead}>
      <button type="button" className={styles.rowToggle} aria-expanded={isOpen} onClick={onToggle}>
        {isOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
        <span className={styles.rowText}>
          <span className={styles.rowLabel}>{label}</span>
          <span className={styles.rowMeta}>
            {meta}
            {isCritical && ' · critical'}
          </span>
        </span>
      </button>
    </th>
  );
}

function Average({ cell, row }: { readonly cell: ComparisonCell | undefined; readonly row: ComparisonRow }): ReactNode {
  if (!cell || cell.average === null) return <span className={styles.none}>{cell && cell.rounds.length > 0 ? 'Not assessed' : EMPTY}</span>;
  return (
    <span className={styles.score}>
      {cell.average.toFixed(1)}
      <span className={styles.outOf}>/{row.scaleMax}</span>
    </span>
  );
}

function CellDetail({ cell }: { readonly cell: ComparisonCell }): ReactNode {
  return (
    <div className={styles.detail}>
      {cell.rounds.map((round) => (
        <div key={round.roundId} className={styles.detailRound}>
          <span className={styles.detailTitle}>
            {round.roundName} · {round.mean === null ? EMPTY : round.mean.toFixed(1)}
          </span>
          {round.scores.map((score) => (
            <span key={score.panelistName} className={score.role === 'observer' ? styles.detailObserver : styles.detailLine}>
              {score.panelistName}
              {score.role === 'observer' ? ' (observer)' : ''}: {score.isNotAssessed ? 'not assessed' : (score.score ?? EMPTY)}
              {score.comment && <span className={styles.comment}>“{score.comment}”</span>}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function OverallDetail({ column }: { readonly column: ComparisonColumn }): ReactNode {
  if (column.rounds.length === 0) return <p className={styles.detailEmpty}>No round held yet.</p>;
  return (
    <div className={styles.detail}>
      {column.rounds.map((round) => (
        <div key={round.roundId} className={styles.detailRound}>
          <span className={styles.detailTitle}>
            {round.name} · {round.isHidden ? 'blind until you submit' : round.roundScore === null ? EMPTY : round.roundScore.toFixed(2)}
            {round.outcome === 'advance' && ' · passed'}
          </span>
          {round.panelists.map((panelist) => (
            <span key={panelist.panelistName} className={panelist.role === 'observer' ? styles.detailObserver : styles.detailLine}>
              {panelist.panelistName}
              {panelist.recommendation && (
                <ToneChip tone={RECOMMENDATION_TONES[panelist.recommendation]}>
                  {RECOMMENDATION_LABELS[panelist.recommendation]}
                </ToneChip>
              )}
              {panelist.notes && <span className={styles.comment}>{panelist.notes}</span>}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function ExtraRow({ label, columns, pinClass, value }: { readonly label: string; readonly columns: readonly ComparisonColumn[]; readonly pinClass: (index: number) => string; readonly value: (column: ComparisonColumn) => ReactNode }): ReactNode {
  return (
    <tr className={styles.extraRow}>
      <th scope="row" className={styles.rowHead}>
        <span className={styles.rowLabelPlain}>{label}</span>
      </th>
      {columns.map((column, index) => (
        <td key={column.applicationId} className={`${styles.cell} ${pinClass(index)}`}>
          {value(column)}
        </td>
      ))}
    </tr>
  );
}

/**
 * S23: the draft opens in the offer builder (OFR-01) — not yet while the selection is on its way.
 * R8 (§L33): with Offers off there is no draft, and the person is simply Selected.
 */
function SelectionOffer({ selection }: { readonly selection: ComparisonSelection }): ReactNode {
  const isOffersOn = useModule('offers');
  if (selection.offerId === null || (!isOffersOn && selection.offerId === PENDING_OFFER_ID)) return SELECTED_STAGE_LABEL;
  if (selection.offerId === PENDING_OFFER_ID) return `Offer draft ${selection.offerReference ?? ''}`;
  return (
    <Link to="/offers/$offerId" params={{ offerId: selection.offerId }}>
      Offer {selection.offerReference}
    </Link>
  );
}
